"use client";

import { useState, useContext, useEffect, useMemo, useRef } from "react";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import MinusIcon from "@/assets/icons/minus";
import PlusIcon from "@/assets/icons/plus";
import {
  ProductDetailsResponse,
  ProductSpecificationInterface,
  ProductVariantInterface,
  VariantCombination,
} from "@/types/product";
import { CartContext, SettingKeys } from "@/global-store/cart-data";
import { settingsService } from "@/services/settings";
import {
  getCheckoutFromItems,
  getFreeShippingMessage,
} from "@/utils/checkout-totals";
import useUserStore from "@/global-store/user";
import { CartInterface, ProductCartVariantInterface } from "@/types/cart";
import { RelatedItems } from "@/app/cart/components/related-items";
import { priceSymbol } from "@/utils/price-count";
import {
  formateProductSpecification,
  formateProductVariant,
} from "@/utils/formate-product-variant";
import {
  isVariantCombinationArray,
  buildVariantGroupsFromCombinations,
  getDefaultSelectionFromCombinations,
  getAllowedOptionIdsForGroup,
  getPriceForSelection,
  areVariantSelectionsEqual,
} from "@/utils/variant-pricing";
import { productService } from "@/services/product";
import { IconButton } from "../icon-button";
import { Typography } from "../typography";
import BreadCrumb from "../breadcrumb/breadcrumb";
import { ProductGallery } from "./ProductGallery";
import { ProductFeatureCards } from "./ProductFeatureCards";
import { AddToCartModal } from "./AddToCartModal";
import { BuyNowModal } from "./BuyNowModal";

const MAX_QUANTITY = 999;

export const ProductDetails = ({
  product,
  relatedProducts,
}: {
  product: ProductDetailsResponse;
  relatedProducts: ProductDetailsResponse[];
}) => {
  const router = useRouter();
  const [selected, setSelected] = useState<ProductCartVariantInterface[]>([]);
  const [combinations, setCombinations] = useState<VariantCombination[] | null>(
    null,
  );
  const [variantGroups, setVariantGroups] = useState<
    ProductCartVariantInterface[]
  >([]);
  const [companyProduct, setCompanyProduct] =
    useState<ProductDetailsResponse | null>(null);

  const displayProduct = companyProduct ?? product;

  const [currentProduct, setCurrentProduct] = useState<
    CartInterface | undefined
  >();
  const [localQuantity, setLocalQuantity] = useState(1);
  const [showAddedToCartModal, setShowAddedToCartModal] = useState(false);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);
  const addToCartInProgressRef = useRef(false);

  const { cartData, addToCart, setBuyNowItems } = useContext(CartContext);
  const user = useUserStore((s) => s.user);

  const maxQty = MAX_QUANTITY;

  useEffect(() => {
    if (!user || !product?.id) return;
    let cancelled = false;
    productService
      .getCompanyProductDetails(String(product.id))
      .then((res) => {
        if (!cancelled && res?.data) setCompanyProduct(res.data);
      })
      .catch(() => undefined);
    // eslint-disable-next-line consistent-return -- useEffect cleanup function
    return () => {
      cancelled = true;
    };
  }, [user, product?.id]);

  const productSpecification = formateProductSpecification(
    displayProduct.properties as ProductSpecificationInterface[],
  );

  const getCartData = () => {
    const isCartData = cartData.find(({ slug, variants }) => {
      const isSame = areVariantSelectionsEqual(variants, selected);
      return isSame && slug === displayProduct.slug;
    });

    setCurrentProduct(isCartData);
  };

  const effectivePrice = (raw: number | string | undefined) => {
    const n =
      typeof raw === "string"
        ? parseFloat(raw)
        : typeof raw === "number"
          ? raw
          : 0;

    return n > 0 ? n : displayProduct.price || 0;
  };

  const flatDiscount = displayProduct.flatDiscount || 0;

  const { data: shippingSettings } = useQuery([SettingKeys.shipping_fee], () =>
    settingsService.get(SettingKeys.shipping_fee),
  );
  const { data: minOrderAmount } = useQuery(
    [SettingKeys.min_order_amount],
    () => settingsService.get(SettingKeys.min_order_amount),
  );

  const checkoutSettings = useMemo(
    () => ({
      shippingValue: Number(shippingSettings?.data?.value ?? 0),
      minOrderValue: Number(minOrderAmount?.data?.value ?? 0),
    }),
    [shippingSettings?.data?.value, minOrderAmount?.data?.value],
  );

  const currentUnitPrice = useMemo(() => {
    let priceToUse = displayProduct.price ?? 0;
    if (combinations && combinations.length) {
      const match = getPriceForSelection(combinations, selected);
      if (match) {
        priceToUse = effectivePrice(match.price);
      } else {
        const filtered = combinations.filter((c) =>
          selected.every((s) => {
            if (!s.variantId?.[0]) return true;
            return c.variant.some(
              (v) =>
                v.groupId.id === s.groupId.id &&
                v.id === s.variantId[0].variantId,
            );
          }),
        );
        if (filtered.length) {
          priceToUse = Math.min(
            ...filtered.map((c) => effectivePrice(c.price)),
          );
        }
      }
    }
    return Math.max(0, priceToUse - flatDiscount);
  }, [displayProduct, combinations, selected, flatDiscount]);

  const getPriceAndComboIds = () => {
    let priceToUse = displayProduct.price ?? 0;
    let comboIds: number[] | undefined;
    if (combinations && combinations.length) {
      const match = getPriceForSelection(combinations, selected);
      if (match) {
        priceToUse = effectivePrice(match.price);
        comboIds = match.combinationIds;
      } else {
        const filtered = combinations.filter((c) =>
          selected.every((s) => {
            if (!s.variantId?.[0]) return true;
            return c.variant.some(
              (v) =>
                v.groupId.id === s.groupId.id &&
                v.id === s.variantId[0].variantId,
            );
          }),
        );
        if (filtered.length) {
          priceToUse = Math.min(
            ...filtered.map((c) => effectivePrice(c.price)),
          );
          comboIds = filtered
            .slice()
            .sort(
              (a, b) => effectivePrice(a.price) - effectivePrice(b.price),
            )[0].id;
        }
      }
    }
    return { priceToUse, comboIds };
  };

  const handleAddToCart = () => {
    if (addToCartInProgressRef.current) return;
    addToCartInProgressRef.current = true;
    const { priceToUse, comboIds } = getPriceAndComboIds();
    addToCart({
      ...displayProduct,
      price: priceToUse,
      quantity: localQuantity,
      variants: selected,
      variantCombinationIds: comboIds,
    });
    setShowAddedToCartModal(true);
    setTimeout(() => {
      addToCartInProgressRef.current = false;
    }, 300);
  };

  const buyNowPreviewItem = useMemo((): CartInterface => {
    const { priceToUse, comboIds } = getPriceAndComboIds();
    return {
      ...displayProduct,
      price: priceToUse,
      quantity: localQuantity,
      variants: selected,
      variantCombinationIds: comboIds,
      flatDiscount,
      shippingFee: displayProduct.shippingFee ?? 0,
    };
  }, [
    displayProduct,
    localQuantity,
    selected,
    flatDiscount,
    combinations,
    currentUnitPrice,
  ]);

  const buyNowCheckout = useMemo(
    () => getCheckoutFromItems([buyNowPreviewItem], checkoutSettings),
    [buyNowPreviewItem, checkoutSettings],
  );

  const buyNowFreeShippingMessage = useMemo(
    () =>
      getFreeShippingMessage(
        buyNowCheckout.price - buyNowCheckout.discountPrice,
        checkoutSettings.minOrderValue,
      ),
    [buyNowCheckout, checkoutSettings.minOrderValue],
  );

  const handleBuyNowProceed = () => {
    if (addToCartInProgressRef.current) return;
    addToCartInProgressRef.current = true;
    const { priceToUse, comboIds } = getPriceAndComboIds();
    const buyNowItem: CartInterface = {
      ...buyNowPreviewItem,
      price: priceToUse,
      variantCombinationIds: comboIds,
    };
    addToCart(buyNowItem); // cart mein bhi add karo taaki cart count update ho
    setBuyNowItems([buyNowItem]);
    setShowBuyNowModal(false);
    router.push("/checkout");
    setTimeout(() => {
      addToCartInProgressRef.current = false;
    }, 300);
  };

  const images = useMemo(
    () =>
      displayProduct?.images?.length
        ? displayProduct.images
        : [displayProduct.image],
    [displayProduct?.images, displayProduct?.image],
  );

  const handleSelectVariant = (variant: ProductCartVariantInterface) => {
    setSelected((prev) => {
      const selectedIndex = prev.findIndex(
        (item) => item.groupId.id === variant.groupId.id,
      );
      if (selectedIndex >= 0) {
        const updatedValue = [...prev];
        updatedValue[selectedIndex] = {
          ...variant,
        };

        return updatedValue;
      }
      return prev;
    });
  };

  useEffect(() => {
    getCartData();
  }, [selected, cartData]);

  useEffect(() => {
    const raw = displayProduct.variants as AnyType;

    if (isVariantCombinationArray(raw)) {
      setCombinations(raw);
      const groups = buildVariantGroupsFromCombinations(raw, displayProduct.id);
      setVariantGroups(groups);

      const defaults = getDefaultSelectionFromCombinations(
        raw,
        displayProduct.id,
      );
      setSelected(defaults);
    } else {
      const groups = formateProductVariant(
        displayProduct.variants as ProductVariantInterface[],
      );
      setVariantGroups(groups);

      if (!currentProduct) {
        const values = groups
          .filter((item) => item.variantId.length)
          .map((item) => ({ ...item, variantId: [item.variantId[0]] }));
        if (values.length) setSelected(values);
      } else {
        setSelected(currentProduct.variants as ProductCartVariantInterface[]);
      }
    }
  }, [displayProduct]);

  const discounted = (p: number) => Math.max(0, p - flatDiscount);
  let displayPriceText = "";

  if (combinations && combinations.length) {
    const match = getPriceForSelection(combinations, selected);
    if (match) {
      displayPriceText = priceSymbol(discounted(effectivePrice(match.price)));
    } else {
      const prices = combinations.map((c) => effectivePrice(c.price));
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      displayPriceText =
        min === max
          ? priceSymbol(discounted(min))
          : `${priceSymbol(discounted(min))} - ${priceSymbol(discounted(max))}`;
    }
  } else {
    displayPriceText = priceSymbol(
      discounted(displayProduct.price || 0),
    );
  }

  // Current undiscounted price (variant or base) for strikethrough and discount %
  const currentUndiscountedPrice = useMemo(() => {
    let priceToUse = displayProduct.price ?? 0;
    if (combinations && combinations.length) {
      const match = getPriceForSelection(combinations, selected);
      if (match) {
        priceToUse = effectivePrice(match.price);
      } else {
        const filtered = combinations.filter((c) =>
          selected.every((s) => {
            if (!s.variantId?.[0]) return true;
            return c.variant.some(
              (v) =>
                v.groupId.id === s.groupId.id &&
                v.id === s.variantId[0].variantId,
            );
          }),
        );
        if (filtered.length) {
          priceToUse = Math.min(
            ...filtered.map((c) => effectivePrice(c.price)),
          );
        }
      }
    }
    return priceToUse;
  }, [displayProduct, combinations, selected]);

  // Discount % = (flatDiscount / current price) so it reflects variant e.g. 50/100=50%, 50/500=10%
  const discountPercentage =
    currentUndiscountedPrice > 0
      ? Math.round((flatDiscount / currentUndiscountedPrice) * 100)
      : 0;

  return (
    <section className="bg-pageBg pb-10">
      <div className="bg-breadcrumb px-2 md:px-4 border-b border-gray-200">
        <BreadCrumb
          routes={[
            { name: "Home", href: "/" },
            { name: "Products", href: "/products" },
            { name: displayProduct.name, href: displayProduct.slug },
          ]}
        />
      </div>
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-center items-start gap-8 mt-2 mb-10">
          <div className="flex flex-col w-full md:w-3/5 lg:w-1/2 ">
            <ProductGallery images={images} />
            <ProductFeatureCards />
          </div>
          <div className="flex flex-col w-full md:w-2/5 lg:w-2/5">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mt-6">
              {displayProduct.categoryId?.name && (
                <span className="inline-block px-3 py-1 rounded-full bg-primary/15 text-primary text-sm font-medium mb-3">
                  {displayProduct.categoryId.name}
                </span>
              )}
              <Typography
                tag="h1"
                className="font-semibold text-2xl lg:text-3xl text-slate-900 mb-2"
              >
                {displayProduct?.name
                  ? displayProduct.name.charAt(0).toUpperCase() +
                    displayProduct.name.slice(1).toLowerCase()
                  : ""}
              </Typography>
              {typeof displayProduct.rating === "number" &&
                displayProduct.reviewCount != null && (
                  <p className="text-gray-600 text-sm mb-2">
                    {displayProduct.rating} / 5
                    {displayProduct.reviewCount > 0 &&
                      ` (${displayProduct.reviewCount} reviews)`}
                  </p>
                )}
              <div className="flex flex-wrap items-baseline gap-2 mb-2">
                <Typography
                  tag="span"
                  className="text-2xl font-semibold text-primary"
                >
                  {displayPriceText}
                </Typography>
                {displayProduct.flatDiscount > 0 && (
                  <Typography
                    tag="span"
                    className="text-lg text-gray-400 line-through"
                  >
                    {priceSymbol(currentUndiscountedPrice)}
                  </Typography>
                )}
                {displayProduct.flatDiscount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                    {discountPercentage}
                    % OFF
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-xs mb-4">
                Inclusive of all taxes
              </p>

              <div className="border-b border-gray-200 my-4" />

              {(displayProduct?.description?.trim() ||
                displayProduct?.mainDescription?.trim()) && (
                <>
                  <Typography
                    tag="h3"
                    className="font-semibold text-gray-900 mb-2"
                  >
                    Product Description
                  </Typography>
                  {displayProduct?.description?.trim() && (
                    <Typography
                      tag="p"
                      className="text-slate-700 text-sm lg:text-base break-words mb-2"
                    >
                      {displayProduct.description}
                    </Typography>
                  )}
                  {displayProduct?.mainDescription?.trim() && (
                    <div
                      className="text-slate-700 text-sm lg:text-base break-words mb-2"
                      dangerouslySetInnerHTML={{
                        __html: displayProduct.mainDescription,
                      }}
                    />
                  )}
                  <p className="text-sm text-green-600 font-medium">In Stock</p>
                  <div className="border-b border-gray-200 my-4" />
                </>
              )}

              {variantGroups.length > 0 && (
                <>
                  <Typography
                    tag="h3"
                    className="font-semibold text-gray-900 mb-3"
                  >
                    Variants
                  </Typography>
                  {variantGroups.map(({ id, groupId, variantId }) => {
                    const allowed = combinations
                      ? getAllowedOptionIdsForGroup(
                          combinations,
                          selected,
                          groupId.id,
                        )
                      : null;
                    return (
                      <div className="flex items-stretch my-3 gap-2" key={id}>
                        <Typography
                          tag="p"
                          className="mr-2 my-1 text-base font-semibold text-gray-700 self-start min-w-20"
                        >
                          {groupId.name}:
                        </Typography>
                        <div className="flex items-center gap-2 flex-wrap">
                          {variantId.map((variant) => {
                            const isSelected = !!selected.find(
                              (item) =>
                                item.groupId.id === groupId.id &&
                                item.variantId[0].variantId ===
                                  variant.variantId,
                            );
                            const isAllowed = allowed
                              ? allowed.has(variant.variantId)
                              : true;
                            return (
                              <button
                                key={variant.id}
                                onClick={() =>
                                  isAllowed &&
                                  handleSelectVariant({
                                    groupId,
                                    id,
                                    variantId: [variant],
                                    productId: displayProduct.id,
                                  })
                                }
                                disabled={!isAllowed}
                                className={clsx(
                                  "transition-all whitespace-nowrap px-3 py-2 border rounded-lg font-semibold text-sm",
                                  isSelected
                                    ? "bg-primary text-white border-primary"
                                    : "bg-white border-gray-300 hover:bg-gray-50",
                                  !isAllowed && "opacity-40 cursor-not-allowed",
                                )}
                                aria-pressed={isSelected}
                              >
                                {variant.value}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  <div className="border-b border-gray-200 my-4" />
                </>
              )}

              {productSpecification.length > 0 && (
                <>
                  <Typography
                    tag="h3"
                    className="font-semibold text-gray-900 mb-3"
                  >
                    Specifications
                  </Typography>
                  <div className="space-y-0">
                    {productSpecification.map(
                      ({ groupId, valueId, id }, idx) => (
                        <div key={id}>
                          {idx > 0 && (
                            <div className="border-b border-gray-100 my-2" />
                          )}
                          <div className="flex justify-between items-center py-2">
                            <span className="font-medium text-gray-700">
                              {groupId?.name}
                            </span>
                            <span className="text-gray-600 text-sm">
                              {valueId?.map((v) => v.value).join(", ") ?? "—"}
                            </span>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                  <div className="border-b border-gray-200 my-4" />
                </>
              )}

              <Typography tag="h3" className="font-semibold text-gray-900 mb-2">
                Quantity
              </Typography>
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden max-w-[160px] mb-4">
                <IconButton
                  onClick={() => setLocalQuantity((q) => Math.max(1, q - 1))}
                  size="large"
                  color="transparent"
                  className="rounded-none border-r border-gray-300 hover:bg-gray-100 "
                >
                  <MinusIcon />
                </IconButton>
                <span className="flex-1 text-center font-medium py-2 min-w-[2.5rem]">
                  {localQuantity}
                </span>
                <IconButton
                  onClick={() =>
                    setLocalQuantity((q) => Math.min(maxQty, q + 1))
                  }
                  size="large"
                  color="transparent"
                  className="rounded-none border-l border-gray-300 hover:bg-gray-100 "
                >
                  <PlusIcon />
                </IconButton>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleAddToCart}
                  className="flex-1 min-w-[140px] py-3 px-4 border border-primary bg-primary/5 text-primary font-semibold rounded-lg hover:bg-primary hover:text-white transition-colors flex items-center justify-center gap-2"
                >
                  Add to Cart
                </button>
                <button
                  type="button"
                  onClick={() => setShowBuyNowModal(true)}
                  className="flex-1 min-w-[140px] py-3 px-4 bg-primary text-white font-semibold rounded-lg hover:bg-cyan-600 transition-colors"
                >
                  Buy Now
                </button>
              </div>
            </div>
          </div>
        </div>
        <hr className="mt-16" />

        {!user && relatedProducts.length ? (
          <RelatedItems products={relatedProducts} />
        ) : null}
      </div>

      <AddToCartModal
        isOpen={showAddedToCartModal}
        onClose={() => setShowAddedToCartModal(false)}
        productName={
          displayProduct?.name
            ? displayProduct.name.charAt(0).toUpperCase() +
              displayProduct.name.slice(1).toLowerCase()
            : ""
        }
        productImage={
          displayProduct?.images?.[0] ?? displayProduct?.image ?? ""
        }
        quantity={localQuantity}
        price={currentUnitPrice}
      />

      <BuyNowModal
        isOpen={showBuyNowModal}
        onClose={() => setShowBuyNowModal(false)}
        productName={
          displayProduct?.name
            ? displayProduct.name.charAt(0).toUpperCase() +
              displayProduct.name.slice(1).toLowerCase()
            : ""
        }
        productImage={
          displayProduct?.images?.[0] ?? displayProduct?.image ?? ""
        }
        quantity={localQuantity}
        unitPrice={currentUnitPrice}
        subtotal={buyNowCheckout.price - buyNowCheckout.discountPrice}
        shipping={buyNowCheckout.shipping}
        total={buyNowCheckout.total}
        freeShippingMessage={buyNowFreeShippingMessage}
        onProceedToCheckout={handleBuyNowProceed}
      />
    </section>
  );
};
